import NewResponsiveImage from "@components/CustomTypography/NewResponsiveImage/NewResponsiveImage"
import AnimatedButton from "@components/WebsiteRevamp/AnimatedButton/AnimatedButton"
import Typography, {
	Size,
	Variant,
} from "@components/WebsiteRevamp/Typography/Typography"
import {
	BackendButton,
	HeroModuleType,
} from "@hooks/WorkWithJson/GetComponentDataByKey/GetComponentDataByKeyTypes"
import clsx from "clsx"
import {AnimatePresence, motion} from "framer-motion"
import {useLayoutEffect, useRef} from "react"

import HeroMainImage from "./HeroMainImage"
import styles from "./HeroSlideContent.module.scss"

type Props = {
	heroItem: HeroModuleType
	styleIndex: number
	currentIndex: number
	titleStyle?: string
}

const getButtonTheme = (
	styleIndex: number,
): "oneHome" | "oneDefault" | "studio" => {
	if (styleIndex === 0) return "oneHome"
	if (styleIndex === 1) return "oneDefault"
	return "studio"
}

const getButtonContrast = (
	styleIndex: number,
	buttonIndex: number,
): "light" | "dark" => {
	if (styleIndex === 0) return "light"
	if (styleIndex === 2 && buttonIndex === 0) return "light"
	return "dark"
}

const hyphenateWordsWiderThanLine = (
	root: HTMLElement,
	hyphenClassName: string,
) => {
	root.querySelectorAll("[data-overflow-hyphen]").forEach((node) => {
		node.replaceWith(document.createTextNode(node.textContent ?? ""))
	})
	root.normalize()

	const maxWidth = root.clientWidth
	if (maxWidth < 10) return

	const measure = document.createElement("span")
	measure.style.position = "absolute"
	measure.style.visibility = "hidden"
	measure.style.whiteSpace = "nowrap"
	measure.style.pointerEvents = "none"
	document.body.appendChild(measure)

	const textNodes: Text[] = []
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
	let current = walker.nextNode()
	while (current) {
		textNodes.push(current as Text)
		current = walker.nextNode()
	}

	textNodes.forEach((textNode) => {
		const value = textNode.nodeValue ?? ""
		if (!value.trim()) return
		const parent = textNode.parentElement
		if (!parent) return

		const style = getComputedStyle(parent)
		measure.style.font = style.font
		measure.style.letterSpacing = style.letterSpacing

		const parts = value.split(/(\s+)/)
		const widths = parts.map((part) => {
			if (!part.trim()) return 0
			measure.textContent = part
			return measure.getBoundingClientRect().width
		})
		if (!widths.some((width) => width > maxWidth)) return

		const fragment = document.createDocumentFragment()
		parts.forEach((part, index) => {
			if (part.trim() && widths[index] > maxWidth) {
				const span = document.createElement("span")
				span.dataset.overflowHyphen = "true"
				span.className = hyphenClassName
				span.textContent = part
				fragment.appendChild(span)
				return
			}
			fragment.appendChild(document.createTextNode(part))
		})
		textNode.parentNode?.replaceChild(fragment, textNode)
	})

	measure.remove()
}

const HeroMain = ({
	heroItem,
	styleIndex,
	currentIndex,
	titleStyle = "",
}: Props) => {
	const buttons = heroItem.buttons ?? []
	const titleRef = useRef<HTMLElement>(null)

	useLayoutEffect(() => {
		const root = titleRef.current
		if (!root) return

		const apply = () => {
			if (!root.isConnected) return
			hyphenateWordsWiderThanLine(root, styles.overflowHyphen)
		}

		apply()
		const observer = new ResizeObserver(apply)
		observer.observe(root)
		void document.fonts?.ready.then(apply)

		return () => observer.disconnect()
	}, [heroItem.title, currentIndex, styleIndex])

	return (
		<div className={styles.main}>
			<div className={styles.textColumn}>
				<AnimatePresence mode={"popLayout"}>
					<motion.div
						key={currentIndex}
						animate={{opacity: 1}}
						exit={{opacity: 0}}
						initial={{opacity: 0}}
						transition={{duration: 0.4, ease: "easeInOut"}}
					>
						<div className={styles.textWrapper}>
							{heroItem.beforeTitlesIcon?.src && (
								<NewResponsiveImage
									alt={
										heroItem.beforeTitlesIcon.alt ||
										heroItem.title
									}
									containerClassName={clsx(
										styles.logoContainer,
										currentIndex === 2 &&
											styles.logoContainerStudio,
									)}
									height={
										heroItem.beforeTitlesIcon.height || 103
									}
									imageClassName={styles.logo}
									src={heroItem.beforeTitlesIcon.src}
									width={
										heroItem.beforeTitlesIcon.width || 505
									}
								/>
							)}
							<Typography
								ref={titleRef}
								as={styleIndex === 0 ? "h1" : "h2"}
								className={clsx(
									styles.title,
									titleStyle,
									styleIndex === 0 && styles.first,
									currentIndex === 2 && styles.third,
								)}
								content={heroItem.title}
								contrast={
									currentIndex === 0 || currentIndex === 2
										? false
										: styleIndex !== 0
								}
								fontWeight={200}
								noPadding
								size={Size.XXL}
								stripHtml={false}
								variant={Variant.Title}
							/>
							<Typography
								className={styles.subtitle}
								content={heroItem.subtitle}
								contrast={
									currentIndex === 0 || currentIndex === 2
										? false
										: styleIndex !== 0
								}
								fontWeight={300}
								noPadding
								size={Size.XS}
								stripHtml={false}
								variant={Variant.Description}
							/>
						</div>
					</motion.div>
				</AnimatePresence>
				{buttons.length > 0 && (
					<div className={styles.buttonContainer}>
						{buttons.map((btn: BackendButton, index: number) => {
							const isFirstSlide = currentIndex === 0
							const isSecondSlide = currentIndex === 1
							const isThirdSlide = currentIndex === 2
							const isPrimary = btn.buttonType === "primary"

							return (
								<AnimatedButton
									key={index}
									className={
										isFirstSlide
											? isPrimary
												? styles.firstSlidePrimary
												: styles.firstSlideSecondary
											: isSecondSlide
											? styles.secondSlidePrimary
											: isThirdSlide
											? isPrimary
												? styles.thirdSlidePrimary
												: styles.thirdSlideSecondary
											: undefined
									}
									contrast={
										(isFirstSlide || isThirdSlide) &&
										isPrimary
											? "light"
											: getButtonContrast(
													styleIndex,
													index,
											  )
									}
									href={btn.link}
									text={btn.text}
									theme={
										isFirstSlide && isPrimary
											? "oneHome"
											: isThirdSlide
											? "studio"
											: getButtonTheme(styleIndex)
									}
									// @ts-expect-error CMS buttonType matches AnimatedButton variants
									variant={btn.buttonType}
								/>
							)
						})}
					</div>
				)}
			</div>
			<HeroMainImage
				alt={heroItem.title}
				currentIndex={currentIndex}
				desktopAsset={heroItem.desktopAsset}
				mobileAsset={heroItem.mobileAsset}
			/>
		</div>
	)
}

export default HeroMain
